import WorkflowRunFilterView from '@UI/Pages/WorkflowRuns/Filters/WorkflowRunFilterView';
import workflowRunFiltersSignal, {
	setWorkflowRunFilters,
} from '@UI/Pages/WorkflowRuns/Filters/Signals/workflowRunFiltersSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';

/**
 * Single place that derives the filters actually in effect for the current agency and caller: a
 * stored value that does not belong to this scope (another agency, another session in the same
 * browser) is replaced by a fresh default rather than shown. Reused by both the content and the
 * filters bar so they always agree on the same value without either recomputing it independently.
 */
const useEffectiveWorkflowRunFilters = (): WorkflowRunFilterView => {
	const stored = useSignalValue(workflowRunFiltersSignal);
	const { currentFacility } = useStrictCurrentFacility();
	const { user } = useStrictAuthenticatedUser();

	const effective = React.useMemo(() => {
		return stored.belongsTo(currentFacility.id, user.id)
			? stored
			: WorkflowRunFilterView.createDefault(currentFacility.id, user.id);
	}, [stored, currentFacility.id, user.id]);

	React.useEffect(() => {
		if (!stored.belongsTo(currentFacility.id, user.id)) {
			setWorkflowRunFilters(effective);
		}
	}, [stored, effective, currentFacility.id, user.id]);

	return effective;
};

export default useEffectiveWorkflowRunFilters;
